import { z } from 'zod';
import { capacityTonnesSchema, kmPerLitreSchema, vehicleColorSchema } from './fleet.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

/** Trucks (PRD → Core objects → Truck; gitaumoses4/chukua#12). */

/** Strip all whitespace, uppercase — so `KDA 123A`, `kda123a` and `KDA  123A` all normalise to the same stored value, `KDA123A`, and the unique constraint catches the duplicate regardless of how it was typed. */
export const truckRegistrationSchema = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .transform((value) => value.replace(/\s+/g, '').toUpperCase());

export const TRUCK_YEAR_FLOOR = 1980;

/** Next year, because a truck is registered before its model year starts. Read from the clock on each call so a long-running server doesn't keep last year's ceiling. */
export function maxTruckYear(now: Date = new Date()): number {
  return now.getFullYear() + 1;
}

/** `POST /trucks` and `PATCH /trucks/:id` share every field except the odometer: `currentOdometerKm` is create-only (the truck's starting reading), and only `POST /trucks/:id/odometer-readings` can move it afterward. */
export const createTruckSchema = z.object({
  registration: truckRegistrationSchema,
  classCode: z.string().trim().min(1).max(32),
  make: z.string().trim().min(1).max(60),
  model: z.string().trim().min(1).max(60),
  year: z
    .number()
    .int()
    .min(TRUCK_YEAR_FLOOR)
    .refine((year) => year <= maxTruckYear(), { message: 'Year cannot be later than next year' }),
  vin: z.string().trim().min(1).max(64),
  engineNumber: z.string().trim().min(1).max(64),
  capacityTonnes: capacityTonnesSchema,
  tankCapacityLitres: z.number().int().positive(),
  hasGenset: z.boolean(),
  isBonded: z.boolean(),
  isHazardousCertified: z.boolean(),
  isCrossBorderEquipped: z.boolean(),
  currentOdometerKm: z.number().int().min(0),
  /** The colour it is painted; omit to keep it (white on a new truck). */
  color: vehicleColorSchema.optional(),
  /** What this truck is expected to burn until it has trips to average (gitaumoses4/chukua#110). Omit to keep it (null on a new truck); null clears it. */
  expectedKmPerLitre: kmPerLitreSchema.nullable().optional(),
});
export type CreateTruckInput = z.infer<typeof createTruckSchema>;

export const updateTruckSchema = createTruckSchema.omit({ currentOdometerKm: true });
export type UpdateTruckInput = z.infer<typeof updateTruckSchema>;

export const recordOdometerReadingSchema = z.object({
  odometerKm: z.number().int().min(0),
});
export type RecordOdometerReadingInput = z.infer<typeof recordOdometerReadingSchema>;

export const TRUCK_COMPLIANCE_STATUSES = ['valid', 'expiring_soon', 'expired', 'none'] as const;
export type TruckComplianceStatus = (typeof TRUCK_COMPLIANCE_STATUSES)[number];

/** Driven entirely by domain events (gitaumoses4/chukua#19) — never set directly by a create/update request. */
export const TRUCK_STATUSES = ['available', 'on_trip', 'in_yard', 'in_workshop', 'off_road'] as const;
export type TruckStatus = (typeof TRUCK_STATUSES)[number];

/** `GET /trucks` — search matches registration, make and model. */
export const truckListQuerySchema = defineListQuery({
  sortFields: ['registration', 'make', 'createdAt'],
  defaultSort: { sort: 'registration', order: 'asc' },
  filters: {
    classCode: listFilter(z.string()),
    compliance: listFilter(z.enum(TRUCK_COMPLIANCE_STATUSES)),
    status: listFilter(z.enum(TRUCK_STATUSES)),
  },
});
export type TruckListQuery = z.infer<typeof truckListQuerySchema>;
